import React, { createContext, useContext, useMemo, useState, useCallback } from "react";

const CartContext = createContext(null);

export const TAX_RATE = 0.07;

export function CartProvider({ children }) {
  const [items, setItems] = useState([]); // [{product_id, name, price, image_url, size, quantity}]
  const [isOpen, setIsOpen] = useState(false);

  const addItem = useCallback((product, quantity = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((p) => p.product_id === product.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          price: product.price,
          image_url: product.image_url,
          size: product.size,
          quantity,
        },
      ];
    });
    setIsOpen(true);
  }, []);

  const updateQuantity = useCallback((product_id, quantity) => {
    setItems((prev) =>
      prev
        .map((p) => (p.product_id === product_id ? { ...p, quantity } : p))
        .filter((p) => p.quantity > 0)
    );
  }, []);

  const removeItem = useCallback((product_id) => {
    setItems((prev) => prev.filter((p) => p.product_id !== product_id));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const { subtotal, tax, total, count } = useMemo(() => {
    const sub = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const t = sub * TAX_RATE;
    return {
      subtotal: Math.round(sub * 100) / 100,
      tax: Math.round(t * 100) / 100,
      total: Math.round((sub + t) * 100) / 100,
      count: items.reduce((n, i) => n + i.quantity, 0),
    };
  }, [items]);

  const value = {
    items,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    isOpen,
    setIsOpen,
    subtotal,
    tax,
    total,
    count,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
